/**
 * @fileoverview Doctor Dashboard page component
 * Displays doctor-specific dashboard with statistics, quick access buttons, and alerts
 */

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { fetchData, HTTP_STATUS, intl } from "dmsbasereact";
import Swal from "sweetalert2";

// Icons
import PeopleIcon from "@mui/icons-material/People";
import DescriptionIcon from "@mui/icons-material/Description";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import LocalPharmacyIcon from "@mui/icons-material/LocalPharmacy";
import HistoryIcon from "@mui/icons-material/History";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import SettingsIcon from "@mui/icons-material/Settings";
import { PatientStatusReason } from "../../constants/PatientStatusReason";
// Components
import { DashboardLayout } from "../../components/Dashboard";
import { PatientDetailModal } from "../../components/PatientDetailModal";

// Utils and Theme
import { APP_ROUTES } from "../../utils/NamedRoutes";
import { APP_COLORS } from "../../theme/appTheme";

/**
 * DoctorDashboard - Main dashboard page for doctors
 *
 * Features:
 * - Stats cards showing patient and prescription statistics
 * - Quick access buttons to main features
 * - Alerts for expiring prescriptions and patients without prescription for 3 months
 *
 * @returns {JSX.Element} The doctor dashboard page
 */
export const DoctorDashboard = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [alerts, setAlerts] = useState(null);
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [selectedPatientDetail, setSelectedPatientDetail] = useState(null);
    const [loadingPatientDetail, setLoadingPatientDetail] = useState(false);

    /**
     * Fetch all dashboard data from the API
     */
    const fetchDashboardData = useCallback(async () => {
        setLoading(true);
        try {
            // Fetch data from all endpoints in parallel
            const [doctorCheckResponse, statsResponse, alertsResponse] = await Promise.all([
                fetchData("/prescription/check_doctor", "GET", true),
                fetchData("/dashboard/doctor/stats", "GET", true),
                fetchData("/dashboard/doctor/alerts", "GET", true),
            ]);

            // Process doctor check response
            if (doctorCheckResponse?.data?.is_doctor === true) {
                // Process stats response
                if (statsResponse.status === HTTP_STATUS.OK_200) {
                    setStats(statsResponse.data?.data);
                }

                // Process alerts response
                if (alertsResponse.status === HTTP_STATUS.OK_200) {
                    setAlerts(alertsResponse.data?.data);
                }
            }
        } catch (err) {
            // Error handling silently
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    /**
     * Format expiring prescriptions for alert card
     */
    const formatExpiringAlerts = () => {
        if (!alerts?.expiring_soon) return [];
        return alerts.expiring_soon.map(item => ({
            id: item.prescription_pk,
            title: item.patient_name,
            description: `${intl.get("ExpiresIn").d("Expires in")} ${item.days_until_expiration} ${intl.get("Days").d("days")}`,
            patient_pk: item.patient_pk
        }));
    };

    /**
     * Format patients without prescription for 3 months
     */
    const formatPatientsWithoutPrescriptionAlerts = () => {
        if (!alerts?.patients_without_prescription) return [];
        return alerts.patients_without_prescription.map(item => ({
            id: item.patient_pk,
            title: item.patient_name,
            description: intl.get("NoPrescriptionFor6Months").d("No prescription for 6 months"),
            patient_pk: item.patient_pk
        }));
    };

    /**
     * Format inactivation and activation history
     */
    const formatInactivationAndActivationAlerts = () => {
        if (!alerts?.inactivation_history) return [];
        return alerts.inactivation_history.map(item => {
            const changeTypeLabel = item.change_type === "ACTIVATED"
                ? intl.get("Activated").d("Activated")
                : intl.get("Deactivated").d("Deactivated");

            const reasonText = item.reason
                ? ` - ${PatientStatusReason.getText(item.reason)}`
                : "";

            const dateText = item.updated_on
                ? new Date(item.updated_on).toLocaleDateString('pt-BR')
                : "";

            return {
                id: `${item.patient_pk}_${item.updated_on}`,
                title: item.patient_name,
                description: `${changeTypeLabel}${reasonText} (${dateText})`,
                patient_pk: item.patient_pk
            };
        });
    };

    /**
     * Handle click on alert items - open patient detail modal
     */
    const handleAlertClick = useCallback(async (item) => {
        if (!item?.patient_pk) return;

        setLoadingPatientDetail(true);
        setDetailModalOpen(true);

        try {
            const response = await fetchData(`/patient/${item.patient_pk}/details`, 'GET', true);
            if (response.status === HTTP_STATUS.OK_200) {
                setSelectedPatientDetail(response.data);
            } else {
                setDetailModalOpen(false);
                Swal.fire({
                    icon: "error",
                    title: intl.get("Error").d("Error"),
                    text: intl.get("failed_load_patient_details").d("Failed to load patient details")
                });
            }
        } catch (error) {
            setDetailModalOpen(false);
            Swal.fire({
                icon: "error",
                title: intl.get("Error").d("Error"),
                text: error.message
            });
        }

        setLoadingPatientDetail(false);
    }, []);

    /**
     * Handle closing the patient detail modal
     */
    const handleCloseDetailModal = useCallback(() => {
        setDetailModalOpen(false);
        setSelectedPatientDetail(null);
    }, []);

    /**
     * Handle saving patient changes
     */
    const handleSavePatient = useCallback(async (patientPk, payload) => {
        try {
            const response = await fetchData(`/patient/${patientPk}`, 'PUT', true, payload);
            return response.data;
        } catch (error) {
            throw error;
        }
    }, []);

    /**
     * Handle patient updated - refresh dashboard data
     */
    const handlePatientUpdated = useCallback(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    // Configure stats cards
    const statsConfig = [
        {
            title: intl.get("TotalPatients").d("Total Patients"),
            value: stats?.total_patients || 0,
            icon: PeopleIcon,
            color: APP_COLORS.primary.main,
            onClick: () => navigate(APP_ROUTES.MyPatients)
        },
        {
            title: intl.get("InactivePatients").d("Inactive Patients"),
            value: stats?.inactive_patients || 0,
            icon: PeopleIcon,
            color: APP_COLORS.primary.main,
            onClick: () => navigate(APP_ROUTES.MyPatients, { state: { tab: 1 } })
        },
        {
            title: intl.get("ActivePrescriptions").d("Active Prescriptions"),
            value: stats?.active_prescriptions || 0,
            icon: DescriptionIcon,
            color: APP_COLORS.system.success
        },
        {
            title: intl.get("ThisMonth").d("This Month"),
            value: stats?.prescriptions_this_month || 0,
            icon: EventAvailableIcon,
            color: APP_COLORS.system.info,
            subtitle: intl.get("NewPrescriptions").d("New prescriptions")
        },
        {
            title: intl.get("Dispensed").d("Dispensed"),
            value: stats?.dispensed_this_month || 0,
            icon: LocalPharmacyIcon,
            color: APP_COLORS.secondary.main,
            subtitle: intl.get("ThisMonth").d("This Month")
        }
    ];

    // Configure quick access buttons
    const quickAccessConfig = [
        {
            label: intl.get("my_patients").d("My Patients"),
            icon: PeopleIcon,
            route: APP_ROUTES.MyPatients,
            color: APP_COLORS.primary.main
        },
        {
            label: intl.get("PrescriptionHistory").d("Prescription History"),
            icon: HistoryIcon,
            route: APP_ROUTES.PrescriptionHistory,
            color: APP_COLORS.secondary.main
        },
        {
            label: intl.get("TrainingVideo").d("Training Video"),
            icon: OndemandVideoIcon,
            route: APP_ROUTES.TrainingVideo,
            color: APP_COLORS.system.info
        },
        {
            label: intl.get("ProfileSettings").d("Profile Settings"),
            icon: SettingsIcon,
            route: APP_ROUTES.ProfileSettings,
            color: APP_COLORS.system.warning
        }
    ];

    // Configure alerts
    const alertsConfig = [
        {
            title: intl.get("ExpiringSoon").d("Expiring Soon"),
            items: formatExpiringAlerts(),
            severity: "warning",
            emptyMessage: intl.get("NoExpiringPrescriptions").d("No prescriptions expiring soon"),
            onItemClick: handleAlertClick
        },
        {
            title: intl.get("InactivationAndActivationHistory").d("Inactivation and Activation History"),
            items: formatInactivationAndActivationAlerts(),
            severity: "warning",
            emptyMessage: intl.get("NoInactivationAndActivationHistory").d("No inactivation and activation history"),
            onItemClick: handleAlertClick
        },
        {
            title: intl.get("PatientsWithoutPrescription").d("Patients without prescription for 6 months"),
            items: formatPatientsWithoutPrescriptionAlerts(),
            severity: "warning",
            emptyMessage: intl.get("NoPatientsWithoutPrescription").d("All patients have recent prescriptions"),
            onItemClick: handleAlertClick
        }
    ];

    return (
        <>
            <DashboardLayout
                welcomeName={stats?.doctor_name}
                loading={loading}
                stats={statsConfig}
                quickAccess={quickAccessConfig}
                alerts={alertsConfig}
            />
            <PatientDetailModal
                open={detailModalOpen}
                onClose={handleCloseDetailModal}
                patient={selectedPatientDetail}
                loading={loadingPatientDetail}
                canEdit={true}
                onSave={handleSavePatient}
                onPatientUpdated={handlePatientUpdated}
            />
        </>
    );
};
